"use client";

import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SEO } from "@/components/seo";
import type { Product } from "@/types/product";
import { DealCard } from "@/components/deals";
import { ProductCard } from "@/components/ui/ProductCard";
import { DealGridSkeleton, MenuScreenSkeleton } from "@/components/skeletons";
import {
  useGetDealsQuery,
  useGetProductsQuery,
  useGetCategoriesQuery,
} from "@/store";

export default function OurMenuScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    searchParams.get("category"),
  );
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const [localSearch, setLocalSearch] = useState(
    searchParams.get("search") || "",
  );

  // Fetch featured deals
  const { data: dealsData, isLoading: dealsLoading } = useGetDealsQuery({
    featured: true,
    isActive: true,
    limit: 6,
  });

  // Fetch products with search filter from API
  // When no search, fetch all products with a high limit to show all categories
  // This ensures all categories with products are visible (not faded)
  const { data: productsData, isLoading: productsLoading } =
    useGetProductsQuery(
      searchQuery ? { search: searchQuery } : { limit: 1000, page: 1 }, // Fetch a large number to get all products for category display
    );
  const { data: categoriesData, isLoading: categoriesLoading } =
    useGetCategoriesQuery({});

  // Keep unavailable products visible so customers can see their stock status.
  const allProducts = productsData?.data || [];
  const categories = categoriesData?.data || [];
  const isLoading = productsLoading || categoriesLoading;
  const featuredDeals = dealsData?.data || [];

  useEffect(() => {
    const category = searchParams.get("category");
    const search = searchParams.get("search");
    setSelectedCategory(category);
    if (search !== null) {
      setSearchQuery(search);
      setLocalSearch(search);
    }
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = localSearch.trim();
    if (trimmed) {
      setSearchParams({ search: trimmed });
      setSearchQuery(trimmed);
      setSelectedCategory(null);
    }
  };

  const clearSearch = () => {
    setLocalSearch("");
    setSearchQuery("");
    setSearchParams({});
  };

  // Filter products by category if selected (search is handled by API)
  let filteredProducts: Product[] = allProducts;

  if (selectedCategory) {
    filteredProducts = allProducts.filter(
      (p) => p.categoryId === selectedCategory,
    );
  }

  const handleCategorySelect = (categoryId: string | null) => {
    setSelectedCategory(categoryId);
    // Clear search when selecting a category
    if (searchQuery) {
      setSearchQuery("");
      setLocalSearch("");
      setSearchParams(categoryId ? { category: categoryId } : {});
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <SEO
        title="Our Menu"
        description="Browse our delicious menu of Nigerian dishes. From jollof rice to suya, we have all your favorite meals available for delivery."
        canonical="/our-menu"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Menu", url: "/our-menu" },
        ]}
      />
      {/* Header - Centered */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Menu</h1>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mt-6">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search for dishes..."
              className="w-full pl-12 pr-12 h-12 text-base"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
            {localSearch && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </form>
        </div>

        {/* Search Results Indicator */}
        {searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 flex items-center justify-center gap-2"
          >
            <span className="text-muted-foreground">
              {filteredProducts.length} result
              {filteredProducts.length !== 1 ? "s" : ""} for "{searchQuery}"
            </span>
            <Button variant="ghost" size="sm" onClick={clearSearch}>
              Clear search
            </Button>
          </motion.div>
        )}
      </motion.div>

      {/* Main Layout: Categories on Left, Menu Items on Right */}
      {isLoading ? (
        <MenuScreenSkeleton />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-8 lg:gap-12">
          {/* Category List - Left Side */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:sticky lg:top-8 h-fit"
          >
            <div className="space-y-2">
              <button
                onClick={() => handleCategorySelect(null)}
                className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                  selectedCategory === null && !searchQuery
                    ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                    : "bg-muted hover:bg-muted/80 text-foreground"
                }`}
              >
                All
              </button>
              {categories.map((category) => {
                // Check if category has products
                const categoryProducts = allProducts.filter(
                  (p) => p.categoryId === category.id,
                );

                // Do not show category if it has no products
                if (categoryProducts.length === 0) return null;

                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg font-medium transition-colors ${
                      selectedCategory === category.id && !searchQuery
                        ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                        : "bg-muted hover:bg-muted/80 text-foreground"
                    }`}
                  >
                    {category.name}
                    <span className="ml-2 text-xs opacity-70">
                      ({categoryProducts.length})
                    </span>
                  </button>
                );
              })}
            </div>
          </motion.aside>

          {/* Menu Items Grid - Right Side */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-8"
          >
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredProducts.map((product, index) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    index={index}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-lg mb-4">
                  {searchQuery
                    ? `No dishes found for "${searchQuery}"`
                    : "No menus found"}
                </p>
                {searchQuery && (
                  <Button variant="outline" onClick={clearSearch}>
                    Clear search
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Featured Deals */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="mt-16"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-orange-500 mb-2">
              <Star className="h-5 w-5 fill-orange-500" />
              <span className="text-xs font-semibold tracking-widest">
                FEATURED DEALS
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">
              Bundles & Specials
            </h2>
            <p className="text-muted-foreground">
              Limited-time offers curated for fast ordering.
            </p>
          </div>
          <Link to="/deals">
            <Button variant="outline">Browse Menu</Button>
          </Link>
        </div>

        {dealsLoading ? (
          <DealGridSkeleton count={3} />
        ) : featuredDeals.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredDeals.map((deal, index) => (
              <DealCard key={deal.id} deal={deal} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center text-muted-foreground">
            Featured deals are coming soon.
          </div>
        )}
      </motion.section>
    </div>
  );
}
